import root from 'window-or-global';
import {
    DLCState,
    ProductBridge,
    ProductInstallState,
    ProductResponseCallback
} from '../productBridge';
import {createClassLogger, Logger} from '../../utils/Logging';

const logger: Logger = createClassLogger('ProductBridge:Mock');

const mockProductInfo = (info: Partial<ProductInstallState>): ProductInstallState =>
    Object.assign<ProductInstallState, Partial<ProductInstallState>>(
        {
            catalogid: '',
            namespace: '',
            appname: '',
            installedversion: '',
            isinitialized: false,
            hasoptionalinstalls: false,
            installalerttext: '',
            installalertverbosity: '',
            isinstalled: false,
            isrunning: false,
            iscorrupt: false,
            ispartiallyinstalled: false,
            isupdating: false,
            iswaitinginline: false,
            isinupdatequeue: false,
            updatestatus: '',
            hasupdate: false,
            progress: 0,
            retrytime: '',
            waittime: '',
            installsizeondisk: 0,
            serverstatus: '',
            serverstatustext: '',
            neverupdate: false,
            isbanned: false,
            isreadytoinstall: false
        },
        info
    );

const executeMockBridgeCall = async <T>(method: string, params: any[], response: T): Promise<T> => {
    logger.info(method, 'MOCKED, with simulated response', {params});
    return await response;
};

const productMock: ProductBridge = {
    getInstalledApps: (): Promise<ProductInstallState[]> =>
        executeMockBridgeCall(
            'getInstalledApps',
            [],
            [
                mockProductInfo({
                    catalogid: 'bk538c739273426aa35a98220e258d55',
                    namespace: 'ut',
                    appname: 'UnrealTournamentDev',
                    isinitialized: true,
                    ispartiallyinstalled: true,
                    installsizeondisk: 4589871852
                }),
                mockProductInfo({
                    appname: 'Fortnite',
                    catalogid: '4fe75bbc5a674f4f9b356b5c90567da5',
                    namespace: 'fn',
                    isinitialized: true,
                    hasoptionalinstalls: true,
                    isinstalled: true,
                    installsizeondisk: 1234567
                }),
                mockProductInfo({
                    appname: 'Battle Breakers',
                    catalogid: 'ipdb1bad6e004b97a7192c1a29f2318a',
                    namespace: 'bt',
                    isinitialized: true,
                    hasoptionalinstalls: true,
                    isinstalled: true,
                    installsizeondisk: 1234567
                })
            ]
        ),

    getApp: async (
        namespace: string,
        catalogId: string,
        appName: string
    ): Promise<ProductInstallState> =>
        executeMockBridgeCall(
            'getApp',
            [namespace, catalogId, appName],
            mockProductInfo({
                catalogid: catalogId,
                namespace,
                appname: appName,
                isinitialized: true,
                hasoptionalinstalls: true,
                installsizeondisk: 1234567
            })
        ),

    install: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('install', [namespace, catalogId, appName, funnelId], true),

    uninstall: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('uninstall', [namespace, catalogId, appName, funnelId], true),

    update: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('update', [namespace, catalogId, appName, funnelId], true),

    pauseInstallation: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('pauseInstallation', [namespace, catalogId, appName, funnelId], true),

    resumeInstallation: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'resumeInstallation',
            [namespace, catalogId, appName, funnelId],
            true
        ),

    cancelInstallation: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'cancelInstallation',
            [namespace, catalogId, appName, funnelId],
            true
        ),

    launch: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('launch', [namespace, catalogId, appName, funnelId], true),

    cancelWaitingRoom: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('cancelWaitingRoom', [namespace, catalogId, appName, funnelId], true),

    showInstallerOptions: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'showInstallerOptions',
            [namespace, catalogId, appName, funnelId],
            true
        ),

    verifyInstallation: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'verifyInstallation',
            [namespace, catalogId, appName, funnelId],
            true
        ),

    createShortcut: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('createShortcut', [namespace, catalogId, appName, funnelId], true),

    getRecentlyPlayed: async (maxResults: number): Promise<ProductInstallState[]> =>
        executeMockBridgeCall(
            'getRecentlyPlayed',
            [maxResults],
            [
                mockProductInfo({
                    appname: 'Fortnite',
                    catalogid: '4fe75bbc5a674f4f9b356b5c90567da5',
                    namespace: 'fn',
                    isinitialized: true,
                    hasoptionalinstalls: true,
                    isinstalled: true,
                    installsizeondisk: 1234567
                }),
                mockProductInfo({
                    catalogid: 'fb39bac8278a4126989f0fe12e7353af',
                    namespace: 'min',
                    appname: 'Min',
                    isinitialized: true,
                    ispartiallyinstalled: true,
                    isinstalled: true,
                    installsizeondisk: 3909656610
                }),
                mockProductInfo({
                    catalogid: 'a5d79a34555b4d55ac4e09b4dde35897',
                    namespace: 'foxglove',
                    appname: 'Foxglove',
                    isinitialized: true,
                    ispartiallyinstalled: false,
                    isinstalled: true,
                    installsizeondisk: 9893276027
                }),
                mockProductInfo({
                    appname: 'Viper',
                    catalogid: 'afa04be9e4e5416c8641cc0398889220',
                    namespace: 'vpr',
                    isinitialized: true,
                    ispartiallyinstalled: false,
                    isinstalled: true,
                    installsizeondisk: 1332609745
                }),
                mockProductInfo({
                    appname: 'Wren',
                    catalogid: 'babd819da5f540c59eb188a25d4beabb',
                    namespace: 'wren',
                    isinitialized: true,
                    ispartiallyinstalled: true,
                    isinstalled: false,
                    installsizeondisk: 0
                })
            ]
        ),

    getGlobalAutoUpdate: async (): Promise<boolean> =>
        executeMockBridgeCall('getGlobalAutoUpdate', [], true),

    setAutoUpdate: async (
        namespace: string,
        catalogId: string,
        appName: string,
        autoUpdate: boolean,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'setAutoUpdate',
            [namespace, catalogId, appName, autoUpdate, funnelId],
            true
        ),

    getAutoUpdate: async (
        namespace: string,
        catalogId: string,
        appName: string
    ): Promise<boolean> =>
        executeMockBridgeCall('getAutoUpdate', [namespace, catalogId, appName], true),

    setCloudSave: async (
        namespace: string,
        catalogId: string,
        appName: string,
        enableSaveSync: boolean,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'setCloudSave',
            [namespace, catalogId, appName, enableSaveSync, funnelId],
            true
        ),

    getCloudSave: async (namespace: string, catalogId: string, appName: string): Promise<boolean> =>
        executeMockBridgeCall('getCloudSave', [namespace, catalogId, appName], true),

    syncCloudSave: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('syncCloudSave', [namespace, catalogId, appName, funnelId], true),

    registerResponseCallback: async (callback: ProductResponseCallback): Promise<void> => {
        logger.info(
            'registerResponseCallback',
            'MOCKED, use __productResponseCallback to trigger callback'
        );
        root.__productResponseCallback = callback;
    },

    goToInstallAlert: async (
        namespace: string,
        catalogId: string,
        appName: string,
        funnelId: string
    ): Promise<boolean> =>
        executeMockBridgeCall('goToInstallAlert', [namespace, catalogId, appName, funnelId], true),

    refetchUserBuilds: async (): Promise<boolean> =>
        executeMockBridgeCall('refetchUserBuilds', [], true),

    setAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string,
        dlcState: DLCState[]
    ): Promise<boolean> =>
        executeMockBridgeCall(
            'setappconfiguration',
            [namespace, catalogId, appName, dlcState],
            true
        ),

    getAppConfiguration: (
        namespace: string,
        catalogId: string,
        appName: string
    ): Promise<DLCState[]> =>
        executeMockBridgeCall('getappconfiguration', [namespace, catalogId, appName], [])
};

export default productMock;
